import React, { useCallback } from 'react';
import { AIcon } from '../../atoms/AIcon/index';
import { AIconSize } from '../../atoms/AIcon/AIcon.types';
import { ALabel, ALabelSize } from '../../atoms/ALabel/index';
import { MCarouselControlsProps, MCarouselControlsType } from './MCarouselControls.types';
import { DEFAULT_PREVIOUS_TEXT, DEFAULT_NEXT_TEXT } from '../../utils/i18nConstants';

const COMPONENT_NAME = 'm-web-ui-kit-MCarouselControls';
const TEST_ID = 'web-ui-kit-MCarouselControls';
const WRAPPER_CLASS_MAP: Record<MCarouselControlsType, string> = {
  [MCarouselControlsType.Dots]: 'm-carousel-control-dot',
  [MCarouselControlsType.Numbers]: 'm-carousel-control-numbers',
};

export const MCarouselControls: React.FC<MCarouselControlsProps> = ({
  type = MCarouselControlsType.Dots,
  imageOverlay = false,
  totalSlides = 5,
  currentSlide = 0,
  onNext,
  onPrev,
  className = '',
  arrows = true,
  loop = false,
}) => {
  const safeCurrentSlide = Math.max(0, Math.min(currentSlide, totalSlides - 1));
  const isFirstSlide = safeCurrentSlide === 0;
  const isLastSlide = safeCurrentSlide === totalSlides - 1;
  const canLoop = loop && totalSlides > 1;
  const wrapperClass = `${WRAPPER_CLASS_MAP[type]}${className ? ` ${className}` : ''}`;

  const handlePrev = useCallback(() => {
    if ((canLoop || !isFirstSlide) && onPrev) {
      onPrev();
    }
  }, [canLoop, isFirstSlide, onPrev]);

  const handleNext = useCallback(() => {
    if ((canLoop || !isLastSlide) && onNext) {
      onNext();
    }
  }, [canLoop, isLastSlide, onNext]);

  const renderDots = () => {
    if (totalSlides < 1) return null;

    return (
      <div className="m-carousel-control-dot-container" aria-hidden="true">
        {Array.from({ length: totalSlides }, (_, index) => (
          <div key={index} className="m-carousel-control-dot-wrapper">
            <div
              className={`m-carousel-control-dot-indicator${index === safeCurrentSlide ? ' is-active' : ''}`}
              aria-current={index === safeCurrentSlide ? 'true' : undefined}
              data-testid="carousel-dot"
            />
          </div>
        ))}
      </div>
    );
  };

  const renderArrow = (direction: 'left' | 'right') => {
    const isLeft = direction === 'left';
    const isDisabled = totalSlides < 1 || (!canLoop && (isLeft ? isFirstSlide : isLastSlide));

    return (
      <button
        type="button"
        className={`m-carousel-control-${direction}-arrow`}
        aria-label={isLeft ? DEFAULT_PREVIOUS_TEXT : DEFAULT_NEXT_TEXT}
        aria-disabled={isDisabled}
        disabled={isDisabled}
        onClick={isLeft ? handlePrev : handleNext}
        data-testid={`carousel-arrow-${direction}`}
      >
        {/* Parent arrow button flips in RTL via global styles; avoid double-flipping the icon. */}
        <AIcon icon={`icon-arrow-${direction}`} size={AIconSize.L} flipInRtl={() => false} />
      </button>
    );
  };

  const renderNumbers = () => {
    if (totalSlides < 1) return null;

    return (
      <div className="m-carousel-control-numbers-container">
        {arrows && renderArrow('left')}
        <ALabel
          text={`${safeCurrentSlide + 1} / ${totalSlides}`}
          size={ALabelSize.XS}
          className="m-carousel-control-number-label"
          data-testid="carousel-number-label"
        />
        {arrows && renderArrow('right')}
      </div>
    );
  };

  const isNumbers = type === MCarouselControlsType.Numbers;

  const renderControls = () => (
    <>
      {isNumbers ? (
        renderNumbers()
      ) : (
        <>
          {arrows && renderArrow('left')}
          {renderDots()}
          {arrows && renderArrow('right')}
        </>
      )}
    </>
  );

  return (
    <div className={wrapperClass} data-component-name={COMPONENT_NAME} data-testid={TEST_ID}>
      {imageOverlay ? <div className="m-carousel-control-overlay-wrapper">{renderControls()}</div> : renderControls()}
    </div>
  );
};

export { MCarouselControlsType } from './MCarouselControls.types';
export type { MCarouselControlsProps } from './MCarouselControls.types';
